<template>
  <b-modal
    id="stockBalanceReportModalHQ"
    ref="stockBalanceReportModalHQ"
    size="xl"
    :title="'ดาวน์โหลดยอดคงเหลือสต๊อก'"
    no-close-on-backdrop
    @close="close"
  >
    <b-form-group label-cols="4" label="ข้อมูล ณ วันที่:" label-align="right">
      <DatePickerField
        v-model="date"
        placeholder="เลือกวันที่ของข้อมูล"
        :is-required="true"
        :is-error="submitted && !date"
        :disabled-date="isFutureDate"
      />
    </b-form-group>

    <b-form-group
      label-cols="4"
      label="สาขา:"
      label-align="right"
      class="scope-group"
    >
      <b-form-radio-group v-model="branchScope">
        <b-form-radio value="ALL">ทั้งหมด</b-form-radio>
        <b-form-radio value="SOME">เลือกบางสาขา</b-form-radio>
      </b-form-radio-group>
    </b-form-group>

    <template v-if="branchScope === 'SOME'">
      <b-form-group
        label-cols="4"
        label=""
        :state="submitted && !isBranchScopeValid ? false : null"
        invalid-feedback="กรุณาเลือกสาขาอย่างน้อย 1 สาขา"
      >
        <div class="search-field-wrapper">
          <b-form-input
            v-model="branchSearchText"
            class="required"
            :class="{ 'is-invalid': submitted && !isBranchScopeValid }"
            placeholder="พิมพ์เพื่อค้นหาสาขา"
            autocomplete="off"
            @focus="showBranchDropdown = true"
            @blur="onBranchBlur"
          />
          <ul v-if="showBranchDropdown && branchMatches.length" class="search-dropdown">
            <li v-for="branch in branchMatches" :key="branch.id" @mousedown.prevent="toggleBranchChip(branch)">
              <b-form-checkbox :checked="isBranchSelected(branch.id)" tabindex="-1">
                {{ branch.name }}
              </b-form-checkbox>
            </li>
          </ul>
        </div>
      </b-form-group>

      <b-form-group v-if="branchChips.length" label-cols="4" label="">
        <div class="chip-list">
          <b-badge v-for="chip in branchChips" :key="'b-' + chip.id" pill variant="light" class="chip">
            {{ chip.name }}
            <i class="fas fa-times chip-remove" @click="removeBranchChip(chip.id)"></i>
          </b-badge>
        </div>
      </b-form-group>
    </template>

    <template #modal-footer>
      <b-button variant="secondary" @click="close">ยกเลิก</b-button>
      <b-button variant="primary" :disabled="isExporting" @click="handleExport">
        <i class="fas fa-file-download"></i>
        {{ isExporting ? "กำลังดาวน์โหลด " + progress + "%" : "ดาวน์โหลด" }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";
import DatePickerField from "@/components/table/stock/fields/DatePickerField.vue";

export default {
  name: "StockBalanceReportModalHQ",
  components: { DatePickerField },
  data() {
    return {
      date: null,
      branchScope: "ALL",
      branchSearchText: "",
      showBranchDropdown: false,
      branchChips: [],
      submitted: false,
      isExporting: false,
      progress: 0,
    };
  },
  computed: {
    ...mapGetters({
      getBranchListForReport: "moduleProductInventory/getBranchListForReport",
    }),
    isBranchScopeValid() {
      return this.branchScope === "ALL" || this.branchChips.length > 0;
    },
    branchMatches() {
      const search = this.branchSearchText.trim().toLowerCase();
      if (!search) return [];
      const selected = new Set(this.branchChips.map((c) => c.id));
      return (this.getBranchListForReport || [])
        .filter((branch) => {
          if (selected.has(branch.id)) return false;
          const name = (branch.name || "").toLowerCase();
          const code = (branch.code || "").toLowerCase();
          return name.includes(search) || code.includes(search);
        })
        .slice(0, 20);
    },
  },
  methods: {
    ...mapActions({
      exportStockBalanceReportHQ: "moduleProductInventory/exportStockBalanceReportHQ",
      fetchBranchListForReport: "moduleProductInventory/fetchBranchListForReport",
    }),
    async show() {
      this.date = null;
      this.branchScope = "ALL";
      this.branchSearchText = "";
      this.branchChips = [];
      this.submitted = false;
      this.$bvModal.show("stockBalanceReportModalHQ");
      try {
        const clinicUrl = this.$route.params.clinicUrl;
        await this.fetchBranchListForReport({ clinicUrl });
      } catch (error) {
        console.error("fetchBranchListForReport error:", error);
      }
    },
    close() {
      this.$bvModal.hide("stockBalanceReportModalHQ");
    },
    isFutureDate(date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const target = new Date(date);
      target.setHours(0, 0, 0, 0);
      return target > today;
    },
    onBranchBlur() {
      setTimeout(() => {
        this.showBranchDropdown = false;
      }, 150);
    },
    isBranchSelected(id) {
      return this.branchChips.some((c) => c.id === id);
    },
    toggleBranchChip(branch) {
      if (this.isBranchSelected(branch.id)) {
        this.removeBranchChip(branch.id);
      } else {
        this.branchChips.push(branch);
      }
    },
    removeBranchChip(id) {
      this.branchChips = this.branchChips.filter((c) => c.id !== id);
    },
    async handleExport() {
      this.submitted = true;
      // Invalid fields are flagged inline under each field (all at once), so
      // there is no separate toast for them.
      if (!this.date || !this.isBranchScopeValid) return;
      this.isExporting = true;
      this.progress = 0;
      try {
        const clinicUrl = this.$route.params.clinicUrl;
        const date = moment(this.date).format("YYYY-MM-DD");
        const branchIds = this.branchScope === "SOME" ? this.branchChips.map((c) => c.id).join(",") : undefined;

        const onProgress = fraction => {
          this.progress = Math.round(fraction * 100);
        };

        const blob = await this.exportStockBalanceReportHQ({ clinicUrl, date, branchIds, onProgress });

        const fileName = `Stock_Balance_Report_HQ_${date}.csv`;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.close();
      } catch (error) {
        console.error("Export stock balance report (HQ) error:", error);
        this.$emit("error", "เกิดข้อผิดพลาดในการ export รายงานสต๊อก");
      } finally {
        this.isExporting = false;
      }
    },
  },
};
</script>

<style scoped>
::v-deep .scope-group .form-row {
  align-items: center;
}

::v-deep .modal-body {
  max-height: 60vh;
  overflow-y: auto;
}

.search-field-wrapper {
  position: relative;
  margin-bottom: 8px;
}

.search-dropdown {
  position: absolute;
  z-index: 20;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 220px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin: 0;
  padding: 4px 0;
  list-style: none;
}

.search-dropdown li {
  padding: 4px 12px;
  cursor: pointer;
}

.search-dropdown li:hover {
  background-color: #f5f6f8;
}

.chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.chip {
  font-weight: normal;
  font-size: 13px;
  padding: 6px 10px;
}

.chip-remove {
  margin-left: 6px;
  cursor: pointer;
}
</style>
